import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { faBuilding } from '@fortawesome/pro-regular-svg-icons/faBuilding'
import convertCentsToDollars from '@lib/utilities/currency/convertCentsToDollars'
import type { ApplyPackagePageProps } from 'app/p/apply-package/[token]/page'
import { AvailabilitySelector } from './AvailabilitySelector'
import { type ScreeningAvailbility } from './AvailabilitySelector/helpers'

type TrialLocationNarrow = {
  address?: string | null
  city?: string | null
  facility?: string | null
  state?: string | null
  zip?: string | null
}

export const AvailabilitySelectorPrompt = ({
  screeningVisitDurationHours,
  selectedSlots,
  setSelectedSlots,
  travelReimbursement,
  trialLocation,
}: {
  screeningVisitDurationHours: number
  selectedSlots: ScreeningAvailbility[]
  setSelectedSlots: (slots: ScreeningAvailbility[]) => void
  travelReimbursement?: ApplyPackagePageProps['trialReimbursements'][0]
  trialLocation: TrialLocationNarrow | null
}) => {
  return (
    <div className='flex flex-col gap-8 md:flex-row'>
      <div className='flex flex-col gap-4 md:w-[40%]'>
        {trialLocation && (
          <LocationSelector
            screeningVisitDurationHours={screeningVisitDurationHours}
            trialLocation={trialLocation}
          />
        )}
        {travelReimbursement && (
          <Text className='text-neutral600' styleName='p-small'>
            Travel cost is covered up to $
            {convertCentsToDollars(travelReimbursement.amount)}
          </Text>
        )}
        {trialLocation && (
          <LocationDetails
            appointmentDuration={screeningVisitDurationHours}
            className='hidden md:block'
            trialLocation={trialLocation}
          />
        )}
      </div>
      <div className='md:w-[60%]'>
        <AvailabilitySelector
          selectedSlots={selectedSlots}
          setSelectedSlots={setSelectedSlots}
        />
      </div>
    </div>
  )
}
const LocationSelector = ({
  className,
  screeningVisitDurationHours,
  trialLocation,
}: {
  className?: string
  screeningVisitDurationHours: number
  trialLocation: TrialLocationNarrow
}) => (
  <div className={className}>
    <div>
      <Text styleName='p-small'>Site Location</Text>
      <div className='rounded-md border border-neutral200 bg-neutral50 px-2'>
        <Text className='line-clamp-1'>
          <Icon className='mr-2  text-neutral600' icon={faBuilding} />
          {trialLocation.facility}
        </Text>
      </div>
      <MobileLocationDetails
        appointmentDuration={screeningVisitDurationHours}
      />
    </div>
  </div>
)
const MobileLocationDetails = ({
  appointmentDuration,
}: {
  appointmentDuration: number
}) => (
  <div className='block md:hidden'>
    <Text className='text-neutral600' styleName='p-small-strong'>
      Hours of operation: 8a-6p
    </Text>

    <Text className='text-neutral600' styleName='p-small-strong'>
      Appointment Duration: {appointmentDuration} hours
    </Text>
  </div>
)
const LocationDetails = ({
  appointmentDuration,
  className,
  trialLocation,
}: {
  appointmentDuration: number
  className?: string
  trialLocation: TrialLocationNarrow
}) => (
  <div className={className}>
    <Text styleName='p-small'>Showing availability for:</Text>
    <div className='flex flex-col gap-4 rounded-md border border-neutral200 p-4'>
      <div>
        <Text className='text-neutral600' styleName='p-small-strong'>
          {trialLocation.facility}
        </Text>
        <Text className='text-neutral600' styleName='p-tiny'>
          {trialLocation.address}
        </Text>
        <Text className='text-neutral600' styleName='p-tiny'>
          {[trialLocation.city, trialLocation.state, trialLocation.zip]
            .filter(Boolean)
            .join(', ')}
        </Text>
      </div>

      <div>
        <Text className='text-neutral600' styleName='p-small-strong'>
          Hours of operation
        </Text>
        <Text className='text-neutral600' styleName='p-tiny'>
          8a-6p
        </Text>
      </div>

      <div>
        <Text className='text-neutral600' styleName='p-small-strong'>
          Appointment Duration
        </Text>
        <Text className='text-neutral600' styleName='p-tiny'>
          {appointmentDuration} hours
        </Text>
      </div>
    </div>
  </div>
)
